import React, { useMemo, useState } from 'react';
import ModalTemplateB from '../../../../../components/_template/modal/ModalTemplateB';
import { logic } from '../../../../../../logic';
import CommentInput from '../../../../../components/_atom/input/CommentInput';
import RadioButton from '../../../../../../legacy-common/atom/button/RadioButton';
import BasicButton from '../../../../../components/_atom/button/BasicButton';
import { ButtonTheme } from '../../../../../components/_atom/button/StyledButton';
import CopyToClipboard from 'react-copy-to-clipboard/lib';
import toast from 'react-hot-toast';
import { ReactComponent as IconCopy } from '../../../../../assets/images/icon_copy.svg';
import iconFailTransaction from '../../../../../assets/images/Icon_fail_transaction.png';
import EnterPinCodeModal from '../../EnterPinCodeModal';
import Checkbox from '../../../../../components/_atom/checkbox/Checkbox';

const STORAGE_TYPE_OPTIONS = [
  {
    id: 'storage-type-kms',
    value: 'KMS',
    label: 'KMS - Managed safely on EQ Hub',
    disabled: false,
  },
  {
    id: 'storage-type-mpc',
    value: 'MPC',
    label: 'MPC - Managed more securely through distributed storage',
    disabled: true,
  },
];

const enum CREATE_ACCOUNT_STEP {
  REGISTRATION,
  COMPLETE,
}

type CreatedAccount = {
  account_id: string;
  project_id: number;
  is_active: boolean; //Account 활성화 여부
  name: string;
  address: string;
  storage_type: string;
  usage_type: string;
  secret_key: string; //생성한 accountManager의 secret key 정보 입니다.
  auth_methods: string[]; //Account의 인증 수단 정보입니다. ex)["PASSWORD", "SECRET_KEY"]
  created_at: string;
  update_at: string;
};
type CreateAccountModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  refreshAccounts: () => void;
};
const CreateAccountModal = (props: CreateAccountModalProps) => {
  const { isOpen, closeModal, refreshAccounts } = props;
  const [step, setStep] = useState<CREATE_ACCOUNT_STEP>(CREATE_ACCOUNT_STEP.REGISTRATION);
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount>();
  const title = useMemo(() => {
    switch (step) {
      case CREATE_ACCOUNT_STEP.REGISTRATION:
        return 'Register Account';
      case CREATE_ACCOUNT_STEP.COMPLETE:
        return 'Account Registration Success';
    }
  }, [step]);
  const onClosed = () => {
    setStep(CREATE_ACCOUNT_STEP.REGISTRATION);
    setCreatedAccount(undefined);
    closeModal();
  };
  return (
    <ModalTemplateB
      isOpen={isOpen}
      title={title}
      showDivideLine={false}
      isDisplayClose={step === 0}
      onRequestClose={onClosed}
      shouldCloseOnOverlayClick={false}
    >
      {step === CREATE_ACCOUNT_STEP.REGISTRATION && (
        <CreateAccount onClosed={onClosed} registeredAccount={createdAccount as CreatedAccount} setRegisteredAccount={setCreatedAccount} setStep={setStep} />
      )}
      {step === CREATE_ACCOUNT_STEP.COMPLETE && (
        <CompleteCreateAccount onClosed={onClosed} createdAccount={createdAccount as CreatedAccount} refreshAccounts={refreshAccounts} />
      )}
    </ModalTemplateB>
  );
};

export default CreateAccountModal;

const CreateAccount = (props: {
  onClosed: () => void;
  registeredAccount: CreatedAccount;
  setRegisteredAccount: (account: CreatedAccount) => void;
  setStep: (step: CREATE_ACCOUNT_STEP) => void;
}) => {
  const { onClosed, registeredAccount, setRegisteredAccount, setStep } = props;
  const [name, setName] = useState<string>('');
  const [storageType, setStorageType] = useState<'KMS' | 'MPC'>();
  const [password, setPassword] = useState<string>('');
  const [isEnterPinCodeModalOpen, setIsEnterPinCodeModalOpen] = useState(false);

  const registerAccount = async () => {
    if (!name || !storageType || !password) {
      return;
    }
    const { result, error } = await logic.accountManager.createAccountManager(name.trim(), storageType, password);
    if (error) {
      return;
    }
    if (result) {
      setRegisteredAccount(result);
      setStep(CREATE_ACCOUNT_STEP.COMPLETE);
      toast.success('New Account Created!');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, paddingLeft: 10, width: 652 }}>
      <EnterPinCodeModal
        isOpen={isEnterPinCodeModalOpen}
        closeModal={() => setIsEnterPinCodeModalOpen(false)}
        connectedWallet={{
          accountName: registeredAccount?.name,
          connectAddress: registeredAccount?.address,
          connectWalletType: 'account',
        }}
        pin={password}
        setPin={setPassword}
      />
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Account Name
          <div className="kit-label required">*</div>
        </div>
        <CommentInput
          value={name}
          style={{ paddingLeft: 10 }}
          placeholder={'Enter your account name'}
          onChange={(e) => {
            setName(e.target.value.trimStart());
          }}
          maxLength={20}
        />
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Account Management Type
          <div className="kit-label required">*</div>
        </div>
        <div className="kit-label-description" style={{ paddingLeft: 10 }}>
          You can choose how to store and manage the account you register.
        </div>
        <div className="radio-buttons" style={{ paddingLeft: 10 }}>
          <div className={`radio-button-container column`}>
            {STORAGE_TYPE_OPTIONS.map((el, key) => {
              if (!el) {
                return null;
              }
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} key={key}>
                  <RadioButton
                    id={el.id ? el.id : el.label}
                    onChange={(e) => setStorageType(e.target.value as 'KMS' | 'MPC')}
                    value={el.value}
                    isSelected={storageType === el.value}
                    label={el.label}
                    labelStyle={{ paddingRight: 0 }}
                    disabled={el.disabled}
                  />
                  {el.disabled && (
                    <div
                      style={{
                        padding: '3px 10px',
                        borderRadius: 100,
                        background: '#C7E2FF',
                        fontSize: 12,
                        fontWeight: 700,
                        lineHeight: '14px',
                        color: '#5888FF',
                      }}
                    >
                      Coming Soon
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">
          Pin Code
          <div className="kit-label required">*</div>
        </div>
        <div className="kit-label-description" style={{ paddingLeft: 10 }}>
          The value you need to enter when trying to use this account in the EQ Hub.
        </div>
        <CommentInput
          value={password}
          type={'password'}
          style={{ paddingLeft: 10, cursor: 'pointer', border: 'solid 2px #e7e9ef' }}
          placeholder={'Enter your pin code'}
          maxLength={6}
          onClick={() => setIsEnterPinCodeModalOpen(true)}
          readOnly
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
        <BasicButton theme={ButtonTheme.NORMAL} onClick={onClosed}>
          Close
        </BasicButton>
        <BasicButton onClick={registerAccount} disabled={!name || !storageType || !password}>
          Register
        </BasicButton>
      </div>
    </div>
  );
};

const CompleteCreateAccount = (props: { onClosed: () => void; createdAccount: CreatedAccount; refreshAccounts: () => void }) => {
  const { onClosed, createdAccount, refreshAccounts } = props;
  const [isConfirmed, setIsConfirmed] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 40, paddingLeft: 10, width: 652 }}>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Account ID</div>
        <div className="kit-value" style={{ paddingLeft: 10, gap: 10 }}>
          {createdAccount.account_id}
          <CopyToClipboard text={createdAccount.account_id} onCopy={() => toast('Account ID Copied', { icon: '👏' })}>
            <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
          </CopyToClipboard>
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Account Name</div>
        <div className="kit-value" style={{ paddingLeft: 10 }}>
          {createdAccount.name}
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div className="kit-label">Address</div>
        <div className="kit-value" style={{ paddingLeft: 10, gap: 10 }}>
          {createdAccount.address}
          <CopyToClipboard text={createdAccount.address} onCopy={() => toast('Address Copied', { icon: '👏' })}>
            <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
          </CopyToClipboard>
        </div>
      </div>
      <div className="kit-label-value-wrapper column">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="kit-label" style={{ minWidth: 'fit-content' }}>
            Secret Key
          </div>
          <img src={iconFailTransaction} alt="icon_fail_transaction" style={{ width: 17, height: 17, objectFit: 'contain' }} />
        </div>
        <div className="kit-value" style={{ paddingLeft: 10, gap: 10, width: '100%', wordBreak: 'break-all' }}>
          {createdAccount.secret_key}
          <CopyToClipboard text={createdAccount.secret_key} onCopy={() => toast('Secret Key Copied', { icon: '👏' })}>
            <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
          </CopyToClipboard>
        </div>
      </div>
      <div style={{ padding: 20, borderRadius: 8, background: '#EFEFEF' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <img src={iconFailTransaction} alt="icon_fail_transaction" style={{ width: 24, height: 24, objectFit: 'contain' }} />
          <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '21px', color: '#E70707' }}>Note : Save your secret key</div>
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            whiteSpace: 'pre-line',
          }}
        >
          {'You will need your secret key when you access your service.\nIf you lose your secret key, all requests will not be available.'}
        </div>
      </div>
      <Checkbox
        id={'confirm-secret'}
        label={'I understand the importance of my secret key and its consequences if lost.'}
        checked={isConfirmed}
        onChange={() => setIsConfirmed(!isConfirmed)}
        onClick={() => setIsConfirmed(!isConfirmed)}
      />
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
        <BasicButton
          theme={ButtonTheme.PRIMARY}
          onClick={() => {
            onClosed();
            refreshAccounts();
          }}
          disabled={!isConfirmed}
        >
          Close
        </BasicButton>
      </div>
    </div>
  );
};
